import { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Calendar,
    Clock,
    MapPin,
    Globe,
    ShieldCheck,
    CheckCircle2,
    AlertTriangle,
    Star,
    X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    getBookingById,
    confirmBookingCompletion,
    disputeBooking,
    submitReview,
    subscribeBookings,
    DISPUTE_REASONS,
} from "@/lib/bookingStore";

const fmt = (n) => "₦" + Number(n || 0).toLocaleString();

const STATUS_CLASS = {
    pending:   "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
    completed: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    released:  "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    disputed:  "bg-destructive/10 text-destructive border-destructive/30",
    cancelled: "bg-muted text-muted-foreground border-border",
};

const BookingDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const [v, setV] = useState(0);

    useEffect(() => subscribeBookings(() => setV((x) => x + 1)), []);

    const booking = useMemo(() => getBookingById(id), [id, v]);

    const [reviewOpen, setReviewOpen] = useState(false);
    const [disputeOpen, setDisputeOpen] = useState(false);
    const [toast, setToast] = useState(null);

    if (!booking) {
        return (
            <div className="screen items-center justify-center" data-testid="booking-not-found">
                <p className="text-[14px] text-muted-foreground">Booking not found.</p>
                <button onClick={() => navigate(-1)} className="mt-3 text-primary text-[13px] font-medium">
                    Go back
                </button>
            </div>
        );
    }

    const isBuyer = user?.id === booking.buyerId;
    const canConfirm = isBuyer && booking.status === "pending";
    const canDispute = isBuyer && (booking.status === "pending" || booking.status === "completed");
    const canReview = isBuyer && booking.status === "released" && !booking.review;

    const handleConfirm = () => {
        const res = confirmBookingCompletion(booking.id);
        if (res.ok) {
            setToast("Funds released to provider");
            setTimeout(() => setToast(null), 2200);
        }
    };

    return (
        <div className="screen" data-testid="booking-detail-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60 flex items-center gap-2">
                <button
                    onClick={() => navigate(-1)}
                    data-testid="booking-detail-back"
                    className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted"
                    aria-label="Back"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="font-display text-[16px] font-semibold flex-1 truncate">
                    Booking
                </h1>
                <span
                    data-testid="booking-status-chip"
                    className={[
                        "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-semibold border",
                        STATUS_CLASS[booking.status] || STATUS_CLASS.pending,
                    ].join(" ")}
                >
                    {booking.status.toUpperCase()}
                </span>
            </header>

            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5 space-y-4">
                {/* Service card */}
                <section className="p-4 rounded-2xl border border-border bg-card flex items-center gap-3" data-testid="booking-service-card">
                    {booking.serviceImage ? (
                        <img src={booking.serviceImage} alt="" className="h-14 w-14 rounded-xl object-cover shrink-0" />
                    ) : (
                        <div className="h-14 w-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <Calendar size={18} />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="font-display text-[15px] font-semibold truncate">{booking.serviceName}</div>
                        <div className="text-[12px] text-muted-foreground truncate">{booking.serviceCategory}</div>
                    </div>
                    <div className="font-display text-[14px] font-semibold shrink-0">{fmt(booking.amount)}</div>
                </section>

                {/* Details */}
                <section className="p-4 rounded-2xl border border-border bg-card space-y-3" data-testid="booking-details-card">
                    <Row icon={Calendar} label="Date" value={booking.date} />
                    <Row icon={Clock} label="Time" value={booking.time} />
                    <Row
                        icon={booking.mode === "virtual" ? Globe : MapPin}
                        label={booking.mode === "virtual" ? "Virtual" : "Location"}
                        value={booking.mode === "virtual" ? "Link shared by provider" : booking.location || "—"}
                    />
                    <Row
                        icon={isBuyer ? undefined : undefined}
                        label={isBuyer ? "Provider" : "Customer"}
                        value={isBuyer ? booking.sellerName : booking.buyerName}
                    />
                    {booking.paymentRef && (
                        <Row label="Reference" value={<span className="font-mono">{booking.paymentRef}</span>} />
                    )}
                </section>

                {/* Escrow state */}
                <section
                    className="p-4 rounded-2xl border border-primary/25 bg-primary/5 flex items-start gap-3"
                    data-testid="booking-escrow-state"
                >
                    <ShieldCheck size={16} className="text-primary mt-0.5 shrink-0" />
                    <div className="text-[12.5px] leading-relaxed">
                        {booking.status === "pending" && (
                            isBuyer
                                ? "Your payment is safely held in escrow. Confirm completion to release funds to the provider."
                                : "You'll receive the funds when the customer confirms completion."
                        )}
                        {booking.status === "released" && (
                            isBuyer
                                ? "Funds have been released to the provider. Thanks!"
                                : "Funds have been moved to your available balance."
                        )}
                        {booking.status === "disputed" && "Dispute opened. Our team will review the issue within 24 hours."}
                        {booking.status === "cancelled" && "Booking cancelled. Any refund has been credited to your wallet."}
                    </div>
                </section>

                {booking.review && (
                    <section className="p-4 rounded-2xl border border-border bg-card" data-testid="booking-review-card">
                        <h3 className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-2">
                            Your review
                        </h3>
                        <div className="flex items-center gap-1 mb-1" aria-label={`${booking.review.rating} of 5`}>
                            {[1, 2, 3, 4, 5].map((n) => (
                                <Star
                                    key={n}
                                    size={15}
                                    className={n <= booking.review.rating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/40"}
                                />
                            ))}
                        </div>
                        {booking.review.comment && (
                            <p className="text-[13px] text-foreground/85">{booking.review.comment}</p>
                        )}
                    </section>
                )}

                {booking.dispute && (
                    <section className="p-4 rounded-2xl border border-destructive/30 bg-destructive/5" data-testid="booking-dispute-card">
                        <h3 className="text-[11px] uppercase tracking-[0.12em] text-destructive font-medium mb-1.5 inline-flex items-center gap-1.5">
                            <AlertTriangle size={12} />
                            Dispute opened
                        </h3>
                        <p className="text-[13px] font-medium">{booking.dispute.reason}</p>
                        {booking.dispute.detail && (
                            <p className="mt-1 text-[12.5px] text-muted-foreground">{booking.dispute.detail}</p>
                        )}
                    </section>
                )}

                {/* Actions (buyer only) */}
                {isBuyer && (
                    <div className="space-y-2" data-testid="booking-actions">
                        {canConfirm && (
                            <button
                                data-testid="booking-confirm-btn"
                                onClick={handleConfirm}
                                className="w-full h-12 rounded-2xl inline-flex items-center justify-center gap-2 text-primary-foreground text-[13.5px] font-medium active:scale-[0.98]"
                                style={{
                                    background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                    boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                                }}
                            >
                                <CheckCircle2 size={16} />
                                Confirm completion · Release funds
                            </button>
                        )}
                        {canReview && (
                            <button
                                data-testid="booking-review-btn"
                                onClick={() => setReviewOpen(true)}
                                className="w-full h-12 rounded-2xl inline-flex items-center justify-center gap-2 bg-secondary border border-border text-foreground text-[13.5px] font-medium hover:bg-muted active:scale-[0.98]"
                            >
                                <Star size={15} />
                                Leave a review
                            </button>
                        )}
                        {canDispute && (
                            <button
                                data-testid="booking-dispute-btn"
                                onClick={() => setDisputeOpen(true)}
                                className="w-full h-12 rounded-2xl inline-flex items-center justify-center gap-2 bg-destructive/5 border border-destructive/30 text-destructive text-[13.5px] font-medium hover:bg-destructive/10 active:scale-[0.98]"
                            >
                                <AlertTriangle size={15} />
                                Report an issue
                            </button>
                        )}
                    </div>
                )}
            </div>

            {reviewOpen && (
                <ReviewSheet booking={booking} onClose={() => setReviewOpen(false)} onDone={() => { setReviewOpen(false); setToast("Review submitted"); setTimeout(() => setToast(null), 1800); }} />
            )}
            {disputeOpen && (
                <DisputeSheet booking={booking} onClose={() => setDisputeOpen(false)} onDone={() => { setDisputeOpen(false); setToast("Issue reported — we'll review"); setTimeout(() => setToast(null), 2400); }} />
            )}

            {toast && (
                <div
                    data-testid="booking-detail-toast"
                    role="status"
                    className="absolute left-1/2 -translate-x-1/2 top-[calc(env(safe-area-inset-top)+12px)] z-50 px-4 py-2.5 rounded-full bg-card border border-border shadow-card inline-flex items-center gap-2 text-[13px] font-medium animate-fade-up"
                >
                    <CheckCircle2 size={16} className="text-emerald-500" />
                    {toast}
                </div>
            )}
        </div>
    );
};

const Row = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3" data-testid={`booking-row-${label.toLowerCase()}`}>
        <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            {Icon ? <Icon size={14} /> : <span className="text-[11px] font-semibold">i</span>}
        </span>
        <div className="min-w-0 flex-1">
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-[13.5px] font-medium break-words">{value}</div>
        </div>
    </div>
);

/* ---------------- Review sheet ---------------- */

const ReviewSheet = ({ booking, onClose, onDone }) => {
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [err, setErr] = useState(null);

    const submit = () => {
        const res = submitReview(booking.id, { rating, comment });
        if (!res.ok) { setErr(res.error); return; }
        onDone?.();
    };

    return (
        <SheetShell testId="review-sheet" onClose={onClose} title="Rate this booking">
            <div className="px-5 py-4 space-y-4">
                <div className="flex items-center justify-center gap-2" data-testid="review-stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button
                            key={n}
                            data-testid={`review-star-${n}`}
                            onClick={() => setRating(n)}
                            className="p-1.5 active:scale-95"
                            aria-label={`${n} star${n > 1 ? "s" : ""}`}
                        >
                            <Star
                                size={28}
                                className={n <= rating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/40"}
                            />
                        </button>
                    ))}
                </div>
                <textarea
                    data-testid="review-comment"
                    rows={4}
                    maxLength={240}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Share a few words about your experience (optional)"
                    className="w-full p-3.5 rounded-2xl border border-border bg-secondary focus:bg-background focus:border-primary outline-none text-[13.5px] resize-none"
                />
                {err && <p className="text-[12px] text-destructive" data-testid="review-error">{err}</p>}
                <button
                    data-testid="review-submit-btn"
                    onClick={submit}
                    disabled={!rating}
                    className="w-full h-12 rounded-2xl text-primary-foreground text-[13.5px] font-medium active:scale-[0.98] disabled:opacity-50"
                    style={{
                        background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                        boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                    }}
                >
                    Submit review
                </button>
            </div>
        </SheetShell>
    );
};

/* ---------------- Dispute sheet ---------------- */

const DisputeSheet = ({ booking, onClose, onDone }) => {
    const [reason, setReason] = useState(null);
    const [detail, setDetail] = useState("");
    const [err, setErr] = useState(null);

    const submit = () => {
        if (!reason) { setErr("Please pick a reason"); return; }
        const res = disputeBooking(booking.id, { reason, detail });
        if (!res.ok) { setErr(res.error); return; }
        onDone?.();
    };

    return (
        <SheetShell testId="dispute-sheet" onClose={onClose} title="Report an issue">
            <div className="px-5 py-4 space-y-3">
                <p className="text-[12.5px] text-muted-foreground">
                    Tell us what went wrong. We'll review and keep your funds safe.
                </p>
                <div className="flex flex-wrap gap-2" data-testid="dispute-reasons">
                    {DISPUTE_REASONS.map((r) => {
                        const active = reason === r.label;
                        return (
                            <button
                                key={r.id}
                                data-testid={`dispute-reason-${r.id}`}
                                onClick={() => { setReason(r.label); setErr(null); }}
                                className={[
                                    "h-9 px-3 rounded-full text-[12px] font-medium border transition-colors",
                                    active
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "bg-secondary text-foreground border-border hover:bg-muted",
                                ].join(" ")}
                            >
                                {r.label}
                            </button>
                        );
                    })}
                </div>
                <textarea
                    data-testid="dispute-detail"
                    rows={4}
                    maxLength={400}
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                    placeholder="Add details that help us review faster (optional)"
                    className="w-full p-3.5 rounded-2xl border border-border bg-secondary focus:bg-background focus:border-primary outline-none text-[13.5px] resize-none"
                />
                {err && <p className="text-[12px] text-destructive" data-testid="dispute-error">{err}</p>}
                <button
                    data-testid="dispute-submit-btn"
                    onClick={submit}
                    className="w-full h-12 rounded-2xl bg-destructive text-destructive-foreground text-[13.5px] font-medium active:scale-[0.98]"
                >
                    Submit report
                </button>
            </div>
        </SheetShell>
    );
};

const SheetShell = ({ testId, onClose, title, children }) => (
    <div className="absolute inset-0 z-50 flex items-end" data-testid={testId} role="dialog" aria-modal="true">
        <button onClick={onClose} aria-label="Close" className="absolute inset-0 bg-black/55 animate-fade-in" />
        <div className="relative z-10 w-full max-h-[90vh] flex flex-col bg-card border-t border-border rounded-t-[28px] shadow-[0_-20px_48px_-12px_rgba(0,0,0,0.4)] animate-slide-up pb-[max(20px,env(safe-area-inset-bottom))]">
            <div className="flex justify-center pt-2.5 pb-1 shrink-0">
                <span className="h-1.5 w-10 rounded-full bg-muted" />
            </div>
            <div className="px-5 pt-1.5 pb-3 flex items-center justify-between border-b border-border/60 shrink-0">
                <h2 className="font-display text-[16px] font-semibold leading-tight">{title}</h2>
                <button onClick={onClose} aria-label="Close" className="h-9 w-9 flex items-center justify-center rounded-xl hover:bg-muted">
                    <X size={18} />
                </button>
            </div>
            <div className="overflow-y-auto no-scrollbar">{children}</div>
        </div>
    </div>
);

export default BookingDetail;
